import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Check,
  Dumbbell,
  Flame,
  HeartPulse,
  Home,
  Ruler,
  Salad,
  Scale,
  Sparkles,
  Target,
  Trophy,
  Utensils,
  Weight,
  Zap,
} from "lucide-react";

const GOALS = [
  { value: "0", title: "کاهش وزن", desc: "چربی ‌سوزی و سبک‌ تر شدن", icon: Flame },
  { value: "1", title: "عضله‌ سازی", desc: "افزایش حجم و فرم بدن", icon: Dumbbell },
  { value: "2", title: "تناسب اندام", desc: "فرم بهتر و بدن متعادل‌ تر", icon: Activity },
  { value: "3", title: "افزایش قدرت", desc: "قوی‌ تر شدن در تمرینات", icon: Trophy },
  { value: "4", title: "استقامت بیشتر", desc: "توان بیشتر در تمرین", icon: Zap },
  { value: "5", title: "بازگشت به تمرین", desc: "شروع دوباره و اصولی", icon: HeartPulse },
];

const GENDERS = [
  { value: "0", title: "مرد", icon: "👨" },
  { value: "1", title: "زن", icon: "👩" },
];

const EXPERIENCES = [
  { value: "0", title: "تازه‌ کار", desc: "تقریبا تجربه تمرین ندارم" },
  { value: "1", title: "مبتدی", desc: "کمتر از ۶ ماه تمرین" },
  { value: "2", title: "متوسط", desc: "۶ ماه تا ۲ سال تمرین" },
  { value: "3", title: "حرفه‌ای", desc: "بیشتر از ۲ سال تمرین" },
];

const EQUIPMENTS = [
  { value: "0", title: "بدون وسیله", desc: "فقط وزن بدن", icon: Home },
  { value: "1", title: "دمبل دارم", desc: "تمرین خانگی بهتر", icon: Weight },
  { value: "2", title: "کش تمرینی دارم", desc: "تمرین سبک و قابل حمل", icon: Activity },
  { value: "3", title: "باشگاه میرم", desc: "دستگاه و وزنه کامل", icon: Dumbbell },
];

const ACTIVITY = [
  { value: "0", title: "کم‌ تحرک", desc: "بیشتر نشسته‌ ام" },
  { value: "1", title: "معمولی", desc: "فعالیت روزانه متوسط" },
  { value: "2", title: "پر تحرک", desc: "کار یا سبک زندگی فعال" },
];

const ALLERGIES = [
  { value: "0", title: "ندارم", icon: "✅" },
  { value: "1", title: "گلوتن", icon: "🌾" },
  { value: "2", title: "لبنیات", icon: "🥛" },
  { value: "3", title: "آجیل", icon: "🥜" },
  { value: "4", title: "تخم‌مرغ", icon: "🥚" },
];

const MEDICAL = [
  { value: "0", title: "ندارم", icon: "✅" },
  { value: "1", title: "دیابت", icon: "🩸" },
  { value: "2", title: "فشار خون", icon: "❤️" },
  { value: "3", title: "قلبی", icon: "🫀" },
  { value: "4", title: "مفصلی", icon: "🦴" },
];

const BODY_FOCUS = [
  { value: "0", title: "کل بدن", icon: "🧍" },
  { value: "1", title: "شکم و میان‌ تنه", icon: "🎯" },
  { value: "2", title: "بالا تنه", icon: "💪" },
  { value: "3", title: "پایین‌ تنه", icon: "🦵" },
  { value: "4", title: "پا و باسن", icon: "🍑" },
];

export default function CampaignPlanWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    journeyMode: "sample",
    requestedPlan: 3,
    planType: 0,
    price: 0,
    isFreeTrial: false,

    gender: "",
    age: "",
    height: "",
    weight: "",

    goal: "",
    fitnessExperience: "",
    bodyFocus: "0",
    activityLevel: "",
    allergies: "",
    medicalConditions: "",

    equipment: "",
    frequency: 3,

    measurements: {
      waist: "",
      arm: "",
      thigh: "",
      chest: "",
      hip: "",
    },
  });

  const totalSteps = 7;
  const progress = Math.round((step / totalSteps) * 100);

  const setF = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const setMeasurement = (key, value) => {
    setForm((p) => ({
      ...p,
      measurements: { ...p.measurements, [key]: normalizeNumber(value) },
    }));
  };

  const meta = useMemo(() => {
    const list = {
      1: ["هدف اصلیت چیه؟", "موجیم اول باید بدونه دنبال چه تغییری هستی"],
      2: ["بدنت رو بهتر بشناسیم", "سن، قد، وزن و جنسیت پایه تحلیل بدن تو هستند"],
      3: ["سطح و محل تمرین", "قرار نیست روز تمرین انتخاب کنی؛ موجیم خودش پیشنهاد میده"],
      4: ["تغذیه و سلامتی", "حساسیت‌ ها و محدودیت‌ ها مسیر برنامه رو تغییر میدن"],
      5: ["تمرکز بدنی", "اگه بخوای، می‌ تونی بگی کدوم بخش بدن برات مهم‌ تره"],
      6: ["اندازه‌ها", "اختیاریه، ولی تحلیل رو دقیق‌ تر و شخصی ‌تر می کنه"],
      7: ["آماده ساخت تحلیل", "اطلاعاتت آمادست؛ حالا موجیم نتیجه اختصاصی رو می‌ سازه"],
    };

    return list[step];
  }, [step]);

  const canNext = useMemo(() => {
    if (step === 1) return form.goal !== "";
    if (step === 2) return form.gender !== "" && form.age && form.height && form.weight;
    if (step === 3) return form.fitnessExperience !== "" && form.equipment !== "";
    if (step === 4) return form.activityLevel !== "" && form.allergies !== "" && form.medicalConditions !== "";
    if (step === 5) return form.bodyFocus !== "";
    return true;
  }, [step, form]);

  const next = () => {
    setError("");

    if (step === 2 && !validateBody(form, setError)) return;

    setStep((s) => Math.min(s + 1, totalSteps));
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(s - 1, 1));
  };

const finish = () => {
  localStorage.setItem("campaignWizardForm", JSON.stringify(form));

  navigate("/body-analysis/otp", {
    state: { form },
  });
};

  return (
    <main dir="rtl" className="font-vazir relative min-h-screen overflow-hidden bg-[#030712] text-white">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,.30),transparent_35%),radial-gradient(circle_at_0%_85%,rgba(37,99,235,.25),transparent_38%),radial-gradient(circle_at_100%_70%,rgba(34,211,238,.16),transparent_30%)]" />
      <div className="fixed inset-0 opacity-[.15] bg-[linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] bg-[size:34px_34px]" />
      <div className="fixed inset-0 bg-gradient-to-b from-transparent via-[#030712]/30 to-[#030712]" />

<section className="relative z-10 mx-auto flex h-screen max-w-md flex-col px-4 pt-5 pb-[96px]">
            <header className="flex items-center justify-between">
          <img src="/logo.png" alt="MOGYM" className="h-10 w-auto" />
          <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-[11px] text-emerald-100 backdrop-blur-2xl">
            {progress}٪ تحلیل
          </div>
        </header>

        <div className="mt-6">
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 flex-1 rounded-full transition ${
                  i + 1 <= step
                    ? "bg-gradient-to-l from-emerald-300 to-cyan-300 shadow-[0_0_16px_rgba(52,211,153,.45)]"
                    : "bg-white/10"
                }`}
              />
            ))}
          </div>

          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            {/* <div className="text-[11px] text-emerald-200">مرحله {step} از {totalSteps}</div> */}
            <h1 className="mt-2 text-3xl font-black leading-tight">{meta[0]}</h1>
            <p className="mt-3 text-sm leading-7 text-white/60">{meta[1]}</p>
          </motion.div>
        </div>

<motion.div
  key={`content-${step}`}
  initial={{ opacity: 0, y: 22, scale: 0.98 }}
  animate={{ opacity: 1, y: 0, scale: 1 }}
  transition={{ duration: 0.28 }}
  className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 pb-4 scrollbar-hide"
>
          {step === 1 && <GoalStep form={form} setF={setF} />}
          {step === 2 && <BodyStep form={form} setF={setF} />}
          {step === 3 && <TrainingStep form={form} setF={setF} />}
          {step === 4 && <HealthStep form={form} setF={setF} />}
          {step === 5 && <FocusStep form={form} setF={setF} />}
          {step === 6 && <MeasurementsStep form={form} setMeasurement={setMeasurement} />}
          {step === 7 && <SummaryStep form={form} />}

          {error && (
            <div className="mt-5 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-center text-sm font-bold text-red-300">
              {error}
            </div>
          )}
        </motion.div>
      </section>

      <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
        <div className="mx-auto flex max-w-md gap-2 rounded-[26px] border border-white/10 bg-black/45 p-3 shadow-2xl backdrop-blur-2xl">

          <button
            type="button"
            disabled={!canNext}
            onClick={step < totalSteps ? next : finish}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 via-green-400 to-cyan-400 text-sm font-black text-black shadow-[0_0_55px_rgba(52,211,153,.45)] disabled:opacity-50"
          >
                        <ArrowRight size={18} />

            {step < totalSteps ? "ادامه تحلیل" : "ساخت نتیجه تحلیل"}
          </button>

          {step > 1 && (
            <button
              type="button"
              onClick={back}
              className="flex h-13 w-28 items-center justify-center gap-1 rounded-2xl border border-white/10 bg-white/5 text-sm font-bold text-white"
            >
              بازگشت
                            <ArrowLeft size={17} />

            </button>
          )}


        </div>
      </div>
    </main>
  );
}

function GoalStep({ form, setF }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {GOALS.map((item) => (
        <BigOption
          key={item.value}
          active={String(form.goal) === item.value}
          icon={item.icon}
          title={item.title}
          desc={item.desc}
          onClick={() => setF("goal", item.value)}
        />
      ))}
    </div>
  );
}

function BodyStep({ form, setF }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        {GENDERS.map((g) => (
          <button
            key={g.value}
            type="button"
            onClick={() => setF("gender", g.value)}
            className={`rounded-[28px] border p-5 text-center transition ${
              String(form.gender) === g.value
                ? "border-emerald-300/60 bg-emerald-400/15 shadow-[0_0_45px_rgba(52,211,153,.22)]"
                : "border-white/10 bg-white/[.05]"
            }`}
          >
            <div className="text-4xl">{g.icon}</div>
            <div className="mt-3 font-black">{g.title}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <NumberInput label="سن" value={form.age} onChange={(v) => setF("age", normalizeNumber(v))} icon={Activity} />
        <NumberInput label="قد" value={form.height} onChange={(v) => setF("height", normalizeNumber(v))} icon={Ruler} />
        <NumberInput label="وزن" value={form.weight} onChange={(v) => setF("weight", normalizeNumber(v))} icon={Scale} />
      </div>
    </div>
  );
}

function TrainingStep({ form, setF }) {
  return (
    <div className="space-y-5">
      <SectionTitle title="سابقه تمرینی" />
      <div className="space-y-3">
        {EXPERIENCES.map((item) => (
          <WideOption
            key={item.value}
            active={String(form.fitnessExperience) === item.value}
            title={item.title}
            desc={item.desc}
            onClick={() => setF("fitnessExperience", item.value)}
          />
        ))}
      </div>

      <SectionTitle title="کجا تمرین می‌کنی؟" />
      <div className="grid grid-cols-2 gap-3">
        {EQUIPMENTS.map((item) => (
          <BigOption
            key={item.value}
            active={String(form.equipment) === item.value}
            icon={item.icon}
            title={item.title}
            desc={item.desc}
            onClick={() => setF("equipment", item.value)}
          />
        ))}
      </div>
    </div>
  );
}

function HealthStep({ form, setF }) {
  return (
    <div className="space-y-5">
      <SectionTitle title="سطح فعالیت روزانه" />
      <div className="space-y-3">
        {ACTIVITY.map((item) => (
          <WideOption
            key={item.value}
            active={String(form.activityLevel) === item.value}
            title={item.title}
            desc={item.desc}
            onClick={() => setF("activityLevel", item.value)}
          />
        ))}
      </div>

      <SectionTitle title="آلرژی غذایی" />
      <div className="grid grid-cols-3 gap-3">
        {ALLERGIES.map((item) => (
          <SmallOption
            key={item.value}
            active={String(form.allergies) === item.value}
            title={item.title}
            icon={item.icon}
            onClick={() => setF("allergies", item.value)}
          />
        ))}
      </div>

      <SectionTitle title="شرایط پزشکی یا محدودیت بدنی" />
      <div className="grid grid-cols-3 gap-3">
        {MEDICAL.map((item) => (
          <SmallOption
            key={item.value}
            active={String(form.medicalConditions) === item.value}
            title={item.title}
            icon={item.icon}
            onClick={() => setF("medicalConditions", item.value)}
          />
        ))}
      </div>
    </div>
  );
}

function FocusStep({ form, setF }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {BODY_FOCUS.map((item) => (
        <SmallOption
          key={item.value}
          active={String(form.bodyFocus) === item.value}
          title={item.title}
          icon={item.icon}
          onClick={() => setF("bodyFocus", item.value)}
        />
      ))}
    </div>
  );
}

function MeasurementsStep({ form, setMeasurement }) {
  return (
    <div className="space-y-4">

      <div className="grid grid-cols-2 gap-3">
        <NumberInput label="دور کمر" value={form.measurements.waist} onChange={(v) => setMeasurement("waist", v)} icon={Ruler} />
        <NumberInput label="دور بازو" value={form.measurements.arm} onChange={(v) => setMeasurement("arm", v)} icon={Ruler} />
        <NumberInput label="دور ران" value={form.measurements.thigh} onChange={(v) => setMeasurement("thigh", v)} icon={Ruler} />
        <NumberInput label="دور سینه" value={form.measurements.chest} onChange={(v) => setMeasurement("chest", v)} icon={Ruler} />
        <NumberInput label="دور باسن" value={form.measurements.hip} onChange={(v) => setMeasurement("hip", v)} icon={Ruler} />
      </div>
    </div>
  );
}

function SummaryStep({ form }) {
  return (
    <div className="space-y-4">
      <div className="rounded-[30px] border border-emerald-400/20 bg-emerald-400/10 p-5 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-emerald-300 text-black">
          <Sparkles size={30} />
        </div>
        <h3 className="mt-4 text-xl font-black">اطلاعات آماده تحلیل است</h3>
        <p className="mt-3 text-sm leading-7 text-white/65">
          حالا موجیم بر اساس شرایط واقعی تو، تحلیل و پیش ‌نمایش برنامه اختصاصی رو آماده میکنه
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SummaryCard label="هدف" value={findTitle(GOALS, form.goal)} />
        <SummaryCard label="سابقه" value={findTitle(EXPERIENCES, form.fitnessExperience)} />
        <SummaryCard label="تمرین" value={findTitle(EQUIPMENTS, form.equipment)} />
        <SummaryCard label="تمرکز" value={findTitle(BODY_FOCUS, form.bodyFocus)} />
      </div>
    </div>
  );
}

function BigOption({ active, icon: Icon, title, desc, onClick }) {
  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      type="button"
      onClick={onClick}
      className={`relative min-h-[138px] rounded-[30px] border p-4 text-right transition ${
        active
          ? "border-emerald-300/60 bg-emerald-400/15 shadow-[0_0_48px_rgba(52,211,153,.24)]"
          : "border-white/10 bg-white/[.05]"
      }`}
    >
      {active && (
        <div className="absolute left-4 top-4 grid h-6 w-6 place-items-center rounded-full bg-emerald-300 text-black">
          <Check size={15} />
        </div>
      )}

      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/8 text-emerald-300">
        <Icon size={24} />
      </div>

      <div className="mt-4 font-black">{title}</div>
      <div className="mt-2 text-xs leading-5 text-white/55">{desc}</div>
    </motion.button>
  );
}

function WideOption({ active, title, desc, onClick }) {
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-[26px] border p-4 text-right transition ${
        active
          ? "border-emerald-300/60 bg-emerald-400/15 shadow-[0_0_40px_rgba(52,211,153,.18)]"
          : "border-white/10 bg-white/[.05]"
      }`}
    >
      <div>
        <div className="font-black">{title}</div>
        <div className="mt-1 text-xs leading-5 text-white/55">{desc}</div>
      </div>

      <div
        className={`grid h-7 w-7 place-items-center rounded-full border ${
          active ? "border-emerald-300 bg-emerald-300 text-black" : "border-white/15"
        }`}
      >
        {active && <Check size={16} />}
      </div>
    </motion.button>
  );
}

function SmallOption({ active, title, icon, onClick }) {
  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      type="button"
      onClick={onClick}
      className={`rounded-[24px] border p-4 text-center transition ${
        active
          ? "border-emerald-300/60 bg-emerald-400/15 shadow-[0_0_38px_rgba(52,211,153,.18)]"
          : "border-white/10 bg-white/[.05]"
      }`}
    >
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 text-xs font-black">{title}</div>
    </motion.button>
  );
}

function NumberInput({ label, value, onChange, icon: Icon }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[.05] p-3">
      <div className="mb-2 flex items-center gap-2 text-[11px] text-white/50">
        <Icon size={14} />
        {label}
      </div>

      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="numeric"
        className="h-12 w-full rounded-2xl border border-white/10 bg-black/25 px-3 text-center text-lg font-black text-white outline-none focus:border-emerald-300/50"
      />
    </div>
  );
}

function SectionTitle({ title }) {
  return <h3 className="text-sm font-black text-emerald-100">{title}</h3>;
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
      <div className="text-[10px] text-white/40">{label}</div>
      <div className="mt-2 text-sm font-black text-white">{value || "-"}</div>
    </div>
  );
}

function validateBody(form, setError) {
  const age = Number(form.age);
  const height = Number(form.height);
  const weight = Number(form.weight);

  if (!Number.isFinite(age) || age < 10 || age > 90) {
    setError("سن باید بین ۱۰ تا ۹۰ سال باشد.");
    return false;
  }

  if (!Number.isFinite(height) || height < 80 || height > 230) {
    setError("قد واردشده معتبر نیست.");
    return false;
  }

  if (!Number.isFinite(weight) || weight < 30 || weight > 250) {
    setError("وزن واردشده معتبر نیست.");
    return false;
  }

  return true;
}

function normalizeNumber(value) {
  return String(value || "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
    .replace(/[^\d]/g, "");
}

function findTitle(list, value) {
  return list.find((x) => String(x.value) === String(value))?.title || "-";
}